/**
 * My contributions (web) — a farmer's view of their OWN harvest contributions.
 *
 * D-021 Path A farmer dashboard, web port of apps/mobile/app/my-contributions.tsx.
 * Scopes to the signed-in user: it reads the JWT `sub` from the access-token
 * claims and fetches GET /contributions?farmerUserId=<self> via the generated
 * orval client `contributionsControllerList`. The server's role='farmer' scope
 * guarantees only the caller's own rows come back.
 *
 * The EMPTY state is the COMMON case for a G1 individual farmer with no
 * cooperative (hence no batches, no contributions). The copy is deliberately
 * reassuring rather than an error.
 *
 * Endpoint: GET /contributions?farmerUserId=<self>
 * Contract from the generated orval client
 * src/api/generated/contributions/contributions.ts (TD-070).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  ListItem,
  Stack,
  Chip,
  Text,
  type ChipColor,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { readAccessTokenClaims, FseApiError } from '../api/client';
import { contributionsControllerList } from '../api/generated/contributions/contributions';
import { groupDigits } from '../lib/format';
import {
  ContributionDtoQualityGrade,
  type ContributionDto,
} from '../api/generated/models';

// Generated `ContributionDtoQualityGrade` is the nullable union ('A'|'B'|'C'|null);
// the GRADE_CHIP record keys need the non-null variant. Derive it from the const.
type QualityGrade = (typeof ContributionDtoQualityGrade)[keyof typeof ContributionDtoQualityGrade];

/** Visual treatment for each quality grade (and the ungraded null case), mapped
 *  onto the kit `Chip` colour union (success → leaf, warning → mango, neutral →
 *  outlined). The label is an i18n key resolved through t() at render (TD-088). */
const GRADE_CHIP: Record<QualityGrade, { color: ChipColor; labelKey: string }> = {
  A: { color: 'success', labelKey: 'myContributions.grade.a' },
  B: { color: 'warning', labelKey: 'myContributions.grade.b' },
  C: { color: 'neutral', labelKey: 'myContributions.grade.c' },
};

/** Shorten a UUID for compact display (batch short-id). */
function shortId(id: string): string {
  return id.slice(0, 8);
}

/** Format a YYYY-MM-DD harvest date to a readable local date, guarding NaN. */
function formatHarvestDate(date: string): string {
  const parsed = new Date(date + 'T00:00:00');
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString();
}

/** Format an ISO-8601 timestamp to a readable local date-time, guarding NaN. */
function formatRecordedAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString();
}

/** A date row rendered as a kit `ListItem`: muted label as the title, value
 *  right-aligned as a trailing `Text`. */
function DetailRow({
  label,
  value,
  leadingIcon,
  noDivider = false,
}: {
  label: string;
  value: ReactNode;
  leadingIcon?: string;
  noDivider?: boolean;
}) {
  return (
    <ListItem
      leadingIcon={leadingIcon}
      title={
        <Text variant="body-medium" color="text.secondary">
          {label}
        </Text>
      }
      trailing={
        <Box sx={{ textAlign: 'right' }}>
          <Text variant="body-medium" color="text.primary">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {value}
            </Box>
          </Text>
        </Box>
      }
      noDivider={noDivider}
    />
  );
}

function ContributionCard({
  contribution,
  t,
}: {
  contribution: ContributionDto;
  t: TFunction;
}) {
  const grade = contribution.qualityGrade;
  const chip = grade ? GRADE_CHIP[grade] : null;

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          {/* Header — batch short-id + grade tag */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Text variant="title-small" color="text.primary">
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                {t('myContributions.batch', { id: shortId(contribution.batchId) })}
              </Box>
            </Text>
            {chip ? (
              <Chip kind="assist" color={chip.color} label={t(chip.labelKey)} />
            ) : (
              <Chip kind="input" color="neutral" label={t('myContributions.ungraded')} />
            )}
          </Box>

          {/* Quantity — qtyKg is already a string; display verbatim. */}
          <Text variant="headline-small">
            {t('myContributions.qtyKg', { qty: groupDigits(contribution.qtyKg) })}
          </Text>

          {/* Dates */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <DetailRow
              leadingIcon="event"
              label={t('myContributions.harvestDate')}
              value={formatHarvestDate(contribution.harvestDate)}
            />
            <DetailRow
              leadingIcon="schedule"
              label={t('myContributions.recorded')}
              value={formatRecordedAt(contribution.recordedAt)}
              noDivider
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function MyContributionsScreen() {
  const { t } = useTranslation();
  const sub = readAccessTokenClaims()?.sub;

  const q = useQuery({
    queryKey: ['my-contributions', { farmerUserId: sub }],
    queryFn: ({ signal }) => contributionsControllerList({ farmerUserId: sub! }, signal),
    enabled: !!sub,
  });

  // Could not derive the caller's identity from the token — surface as an error
  // rather than firing a query we know will be rejected.
  if (!sub) {
    return (
      <Stack spacing={3}>
        <Box>
          <Text variant="headline-small" as="h2">
            {t('myContributions.title')}
          </Text>
          <Box sx={{ mt: 0.5 }}>
            <Text variant="body-medium" color="text.secondary">
              {t('myContributions.subtitle')}
            </Text>
          </Box>
        </Box>
        <Alert severity="error">{t('myContributions.noIdentity')}</Alert>
      </Stack>
    );
  }

  const contributions = q.data?.data ?? [];
  const total = q.data?.pagination.total;

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('myContributions.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {typeof total === 'number'
              ? t('myContributions.subtitleWithCount', { total })
              : t('myContributions.subtitle')}
          </Text>
        </Box>
      </Box>

      {q.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : q.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => q.refetch()}>
              {t('myContributions.retry')}
            </Button>
          }
        >
          {t('myContributions.loadError', {
            error: q.error instanceof FseApiError ? q.error.message : String(q.error),
          })}
        </Alert>
      ) : contributions.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h3">
                {t('myContributions.empty.title')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t('myContributions.empty.body')}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {contributions.map((c) => (
            <ContributionCard key={c.id} contribution={c} t={t} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
